import {
    BlockStack,
    Box,
    Checkbox,
    IndexTable,
    InlineStack,
    Layout,
    Page,
    SkeletonBodyText,
    SkeletonPage,
    Spinner,
    Tabs,
    Text,
    TextField,
} from "@shopify/polaris";
import { SafeSaveBar as SaveBar } from "../../app/components/SafeAppBridge";
import { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { UserSetting } from "../../app/models/UserSetting";
import { useStore } from "../../app/stores/store";
import { SettingCheckbox, SettingsCard } from "./SettingsControls";
import StorefrontTranslationsSettings from "./StorefrontTranslationsSettings";

const normalizeIds = (ids: number[]) => [...ids].sort((a, b) => a - b).join("|");

const normalizeVendorRules = (rules: { shopifyVendor?: string; skuPrefix?: string; normalizeSku?: boolean }[]) =>
    JSON.stringify(
        [...rules]
            .map((rule) => ({
                shopifyVendor: rule.shopifyVendor || "",
                skuPrefix: rule.skuPrefix || "",
                normalizeSku: !!rule.normalizeSku,
            }))
            .sort((left, right) => left.shopifyVendor.localeCompare(right.shopifyVendor))
    );

const SettingsPage: React.FC = () => {
    const { settingStore } = useStore();
    const {
        currentSettings: settings,
        loadingUpdate,
        loadingInitial,
        loadSettings,
        inventoryLocations,
        loadingInventoryLocations,
        setInventoryLocationSelected,
        selectedInventoryLocationIds,
        loadInventoryLocations,
        vendorSkuRules,
        loadingVendorSkuRules,
        loadVendorSkuRules,
        setVendorSkuPrefix,
        setVendorSkuNormalize,
    } = settingStore;

    const [settingsLoaded, setSettingsLoaded] = useState(false);
    const [updateInProgress, setUpdateInProgress] = useState(false);
    const [inventoryAutoMapEnabled, setInventoryAutoMapEnabled] = useState(false);
    const [taggingTagPrefixEnabled, setTaggingTagPrefixEnabled] = useState(true);
    const [vendorRulesBaseline, setVendorRulesBaseline] = useState<string | undefined>(undefined);
    const [vendorRulesLoadStarted, setVendorRulesLoadStarted] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);

    const tabs = [
        { id: "general-tab", content: "General", accessibilityLabel: "General settings", panelID: "general-panel" },
        { id: "sync-inventory-tab", content: "Sync / inventory", accessibilityLabel: "Sync and inventory settings", panelID: "sync-inventory-panel" },
        { id: "vendor-sku-tab", content: "Vendor SKU rules", accessibilityLabel: "Vendor SKU rule settings", panelID: "vendor-sku-panel" },
        // Also expose translations from app settings for merchants who look here first.
        { id: "translations-tab", content: "Translations", accessibilityLabel: "Storefront translations", panelID: "translations-panel" },
        { id: "advanced-app-tab", content: "Advanced app settings", accessibilityLabel: "Advanced app settings", panelID: "advanced-app-panel" },
    ];

    useEffect(() => {
        const fetchSettings = async () => {
            await loadSettings();
            setSettingsLoaded(true);
        };

        if (!settingsLoaded) fetchSettings();
    }, [settingsLoaded, loadSettings]);

    useEffect(() => {
        if (settings) {
            setInventoryAutoMapEnabled(!!settings.inventoryAutoMapEnabled);
            setTaggingTagPrefixEnabled(settings.useYmmPrefixForProductTags ?? true);
        }
    }, [settings]);

    useEffect(() => {
        loadInventoryLocations();
    }, [loadInventoryLocations]);

    useEffect(() => {
        loadVendorSkuRules();
    }, [loadVendorSkuRules]);

    useEffect(() => {
        if (loadingVendorSkuRules) {
            setVendorRulesLoadStarted(true);
            return;
        }

        if (vendorRulesLoadStarted && vendorRulesBaseline === undefined) {
            setVendorRulesBaseline(normalizeVendorRules(vendorSkuRules));
        }
    }, [loadingVendorSkuRules, vendorRulesBaseline, vendorRulesLoadStarted, vendorSkuRules]);

    const handleInventoryAutoMapChange = useCallback((newChecked: boolean) => {
        setInventoryAutoMapEnabled(newChecked);
    }, []);

    const handleTaggingTagPrefixChange = useCallback((newChecked: boolean) => {
        setTaggingTagPrefixEnabled(newChecked);
    }, []);

    const handleInventoryLocationToggle = useCallback(
        (id: number, checked: boolean) => {
            if (!inventoryAutoMapEnabled) return;
            setInventoryLocationSelected(id, checked);
        },
        [inventoryAutoMapEnabled, setInventoryLocationSelected]
    );

    const inventoryRows = useMemo(
        () =>
            inventoryLocations.map((loc, index) => {
                const statusBits: string[] = [];
                if (loc.isPrimary) statusBits.push("Primary");
                if (!loc.isActive) statusBits.push("Inactive");
                const status = statusBits.join(" / ") || "-";

                return (
                    <IndexTable.Row id={String(loc.id)} key={loc.id} position={index}>
                        <IndexTable.Cell>
                            <Checkbox
                                label=""
                                checked={!!loc.selected}
                                onChange={(newChecked) => handleInventoryLocationToggle(loc.id, newChecked)}
                                disabled={!inventoryAutoMapEnabled}
                            />
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                            <Text as="span" variant="bodyMd" fontWeight="bold">
                                {loc.name}
                            </Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                            <Text as="span" variant="bodySm" tone="subdued">
                                {loc.address || "-"}
                            </Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                            <Text as="span" variant="bodySm" tone="subdued">
                                {status}
                            </Text>
                        </IndexTable.Cell>
                    </IndexTable.Row>
                );
            }),
        [inventoryLocations, handleInventoryLocationToggle, inventoryAutoMapEnabled]
    );

    const vendorRows = useMemo(
        () =>
            vendorSkuRules.map((rule, index) => (
                <IndexTable.Row id={`${rule.shopifyVendor}-${index}`} key={rule.shopifyVendor} position={index}>
                    <IndexTable.Cell>
                        <Text as="span" variant="bodyMd" fontWeight="bold">
                            {rule.shopifyVendor}
                        </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                        <TextField
                            label="SKU Prefix"
                            labelHidden
                            autoComplete="off"
                            value={rule.skuPrefix || ""}
                            onChange={(value) => setVendorSkuPrefix(rule.shopifyVendor, value)}
                            placeholder="Optional prefix"
                        />
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                        <Checkbox
                            label=""
                            checked={!!rule.normalizeSku}
                            onChange={(value) => setVendorSkuNormalize(rule.shopifyVendor, value)}
                        />
                    </IndexTable.Cell>
                </IndexTable.Row>
            )),
        [vendorSkuRules, setVendorSkuPrefix, setVendorSkuNormalize]
    );

    const currentVendorSnapshot = normalizeVendorRules(vendorSkuRules);
    const vendorRulesChanged = vendorRulesBaseline !== undefined && currentVendorSnapshot !== vendorRulesBaseline;
    const effectiveInventoryIds = loadingInventoryLocations
        ? settings?.inventoryLocationIds ?? []
        : selectedInventoryLocationIds;
    const currentInventoryIds = normalizeIds(effectiveInventoryIds);
    const baselineInventoryIds = normalizeIds(settings?.inventoryLocationIds || []);
    const hasUnsavedChanges =
        JSON.stringify({
            taggingTagPrefixEnabled,
            inventoryAutoMapEnabled,
            inventoryLocationIds: currentInventoryIds,
            vendorRules: vendorRulesChanged ? currentVendorSnapshot : vendorRulesBaseline ?? currentVendorSnapshot,
        }) !== JSON.stringify({
            taggingTagPrefixEnabled: settings?.useYmmPrefixForProductTags ?? true,
            inventoryAutoMapEnabled: !!settings?.inventoryAutoMapEnabled,
            inventoryLocationIds: baselineInventoryIds,
            vendorRules: vendorRulesBaseline ?? currentVendorSnapshot,
        });

    const handleDiscardSettings = useCallback(async () => {
        setInventoryAutoMapEnabled(!!settings?.inventoryAutoMapEnabled);
        setTaggingTagPrefixEnabled(settings?.useYmmPrefixForProductTags ?? true);
        setVendorRulesBaseline(undefined);
        setVendorRulesLoadStarted(false);
        await Promise.all([loadVendorSkuRules(), loadInventoryLocations()]);
    }, [loadInventoryLocations, loadVendorSkuRules, settings]);

    const handleSaveSettings = async () => {
        const inventoryLocationIds = loadingInventoryLocations
            ? settings?.inventoryLocationIds ?? []
            : selectedInventoryLocationIds;
        const newSetting: UserSetting = {
            searchEnablePersistentFiltering: !!settings?.searchEnablePersistentFiltering,
            inventoryAutoMapEnabled,
            useYmmPrefixForProductTags: taggingTagPrefixEnabled,
            submodelEnabled: settings?.submodelEnabled ?? false,
            inventoryLocationIds,
        };

        if (settings?.id !== undefined) {
            newSetting.id = settings.id;
        }

        setUpdateInProgress(true);
        try {
            const settingSaved = settingStore.updateSettings(newSetting);
            const vendorSaved = vendorRulesChanged
                ? settingStore.updateVendorSkuRules(vendorSkuRules)
                : Promise.resolve(true);

            const [, savedVendorRules] = await Promise.all([settingSaved, vendorSaved]);
            if (savedVendorRules) {
                setVendorRulesBaseline(normalizeVendorRules(vendorSkuRules));
            }
        } finally {
            setUpdateInProgress(false);
        }
    };

    if (!settingsLoaded || loadingInitial) {
        return (
            <SkeletonPage title="Settings">
                <Layout>
                    <Layout.Section>
                        <Box padding="400">
                            <SkeletonBodyText />
                        </Box>
                    </Layout.Section>
                </Layout>
            </SkeletonPage>
        );
    }

    return (
        <>
            <SaveBar id="settings-save-bar" open={hasUnsavedChanges}>
                <button onClick={handleDiscardSettings} disabled={updateInProgress || loadingUpdate}>
                    Discard
                </button>
                <button variant="primary" onClick={handleSaveSettings} disabled={updateInProgress || loadingUpdate} loading={updateInProgress || loadingUpdate}>
                    Save
                </button>
            </SaveBar>

            <Page fullWidth title="Settings">
                <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
                    {selectedTab === 0 && (
                        <Layout>
                            <Layout.Section>
                                <SettingsCard
                                    title="Tag generation"
                                    description="Control how product fitment tags are written and matched by app workflows."
                                >
                                    <SettingCheckbox
                                        label="Enable tag prefix (ymm_)"
                                        checked={taggingTagPrefixEnabled}
                                        onChange={handleTaggingTagPrefixChange}
                                        description="Recommended for most stores. The prefix prevents vehicle tags from colliding with regular Shopify tags."
                                        help="Disable only if a store intentionally uses unprefixed vehicle tags."
                                    />
                                </SettingsCard>
                            </Layout.Section>
                        </Layout>
                    )}

                    {selectedTab === 1 && (
                        <Layout>
                            <Layout.Section>
                                <SettingsCard
                                    title="Inventory sync"
                                    description="Choose default inventory locations that should be mapped to synced products."
                                >
                                    <BlockStack gap="300">
                                        <Checkbox
                                            label="Ensure selected locations are mapped to every product"
                                            checked={inventoryAutoMapEnabled}
                                            onChange={handleInventoryAutoMapChange}
                                        />
                                        <Text as="p" variant="bodySm" tone="subdued">
                                            Existing product-location mappings remain unchanged; this only supplements missing locations.
                                        </Text>

                                        <Box paddingBlockStart="200">
                                            {loadingInventoryLocations ? (
                                                <InlineStack gap="200" align="center">
                                                    <Spinner accessibilityLabel="Loading inventory locations" size="small" />
                                                    <Text as="span" variant="bodyMd">
                                                        Loading inventory locations...
                                                    </Text>
                                                </InlineStack>
                                            ) : inventoryLocations.length === 0 ? (
                                                <Text as="p" variant="bodyMd">
                                                    No inventory locations are currently available for this shop.
                                                </Text>
                                            ) : (
                                                <IndexTable
                                                    resourceName={{ singular: "location", plural: "locations" }}
                                                    itemCount={inventoryLocations.length}
                                                    selectable={false}
                                                    headings={[
                                                        { title: "Selected" },
                                                        { title: "Location" },
                                                        { title: "Address" },
                                                        { title: "Status" },
                                                    ]}
                                                >
                                                    {inventoryRows}
                                                </IndexTable>
                                            )}
                                        </Box>
                                    </BlockStack>
                                </SettingsCard>
                            </Layout.Section>
                        </Layout>
                    )}

                    {selectedTab === 2 && (
                        <Layout>
                            <Layout.Section>
                                <SettingsCard
                                    title="Vendor SKU rules"
                                    description="Control how imported part numbers are mapped to Shopify SKUs for each vendor."
                                >
                                    {loadingVendorSkuRules ? (
                                        <InlineStack gap="200" align="center">
                                            <Spinner accessibilityLabel="Loading vendor SKU rules" size="small" />
                                            <Text as="span" variant="bodyMd">
                                                Loading vendor SKU rules...
                                            </Text>
                                        </InlineStack>
                                    ) : vendorSkuRules.length === 0 ? (
                                        <Text as="p" variant="bodyMd">
                                            No vendors found for this shop yet.
                                        </Text>
                                    ) : (
                                        <IndexTable
                                            resourceName={{ singular: "vendor", plural: "vendors" }}
                                            itemCount={vendorSkuRules.length}
                                            selectable={false}
                                            headings={[
                                                { title: "Vendor" },
                                                { title: "SKU Prefix" },
                                                { title: "Normalize SKU" },
                                            ]}
                                        >
                                            {vendorRows}
                                        </IndexTable>
                                    )}
                                </SettingsCard>
                            </Layout.Section>
                        </Layout>
                    )}

                    {selectedTab === 3 && (
                        <Layout>
                            <Layout.Section>
                                <StorefrontTranslationsSettings />
                            </Layout.Section>
                        </Layout>
                    )}

                    {selectedTab === 4 && (
                        <Layout>
                            <Layout.Section>
                                <SettingsCard title="Advanced app settings">
                                    <Text as="p" tone="subdued">
                                        No advanced app-wide settings are currently exposed for this store.
                                    </Text>
                                </SettingsCard>
                            </Layout.Section>
                        </Layout>
                    )}
                </Tabs>
            </Page>
        </>
    );
};

export default observer(SettingsPage);
